import { useCallback, useContext } from 'react';

import {
  useLazyExportToExcelFinancialReportsQuery,
  useLazyExportToExcelFinancialDetailsOfExpensesReportQuery,
  useLazyExportToExcelFinancialExpensesByArticlesReportQuery,
  useLazyExportToExcelFinancialFactImplReportQuery,
  useLazyExportToExcelFinancialIncomesReportQuery,
  useLazyExportToExcelFinancialPnLReportQuery,
} from '@app/api/Analytics';
import { useLazyExportToExcelQuery } from '@app/api/ClientPayments';
import { useExportServicesToExcelByIdsMutation } from '@app/api/ClientServices';
import { useLazyExportEmployeeSalaryToExcelQuery } from '@app/api/EmployeeSalary';
import { FRONT_WARNING, XLSX } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NClientServices, NFiles, NHooks } from '@app/types';

export const useExportToExcel = ({ setLoader }: NHooks.UseServicesExport) => {
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);

  const [exportToExcel] = useLazyExportToExcelQuery();
  const [exportServicesToExcelByIds] = useExportServicesToExcelByIdsMutation();
  const [exportToExcelFinancialPnLReport] =
    useLazyExportToExcelFinancialPnLReportQuery();
  const [exportToExcelFinancialIncomesReport] =
    useLazyExportToExcelFinancialIncomesReportQuery();
  const [exportToExcelFinancialSalaryReportQuery] =
    useLazyExportEmployeeSalaryToExcelQuery();
  const [ExportToExcelFinancialDetailsOfExpensesReport] =
    useLazyExportToExcelFinancialDetailsOfExpensesReportQuery();
  const [exportToExcelFinancialFactImpl] =
    useLazyExportToExcelFinancialFactImplReportQuery();
  const [exportToExcelFinancialExpensesByArticlesReport] =
    useLazyExportToExcelFinancialExpensesByArticlesReportQuery();
  const [exportToExcelFinancialReports] =
    useLazyExportToExcelFinancialReportsQuery();

  const handleFileData = useCallback(
    (file: NFiles.ModelFile) => {
      if (!file)
        return showNotification(
          'При получении файла произошла ошибка',
          FRONT_WARNING
        );

      const clientServicesPage = document.querySelector('.bc-export-block');
      const downloadButton = document.createElement('a');
      downloadButton.setAttribute('href', `data:${XLSX};base64,${file.Data}`);

      downloadButton.setAttribute('download', file?.Name || 'Без имени');
      downloadButton.setAttribute('style', 'display: hidden');
      clientServicesPage?.appendChild(downloadButton);
      downloadButton.click();
    },
    [showNotification]
  );

  const exportClientPaymentsToExcel = useCallback(
    async (searchId: number | null) => {
      setLoader(true);
      try {
        const result = await exportToExcel({
          searchId: searchId || 0,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        handleFileData(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoader(false);
      }
    },
    [activeBranchId, exportToExcel, handleFileData, showNotification, setLoader]
  );

  const exportToExcelByIds = useCallback(
    async (_allServices: Array<NClientServices.Service>) => {
      try {
        const result = await exportServicesToExcelByIds({
          ActiveBranchId: activeBranchId,
          data: _allServices.map(({ Id }) => Id),
        }).unwrap();

        if (result) {
          handleFileData(result);
        } else {
          showNotification(
            'При получении файла произошла ошибка',
            FRONT_WARNING
          );
        }
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoader(false);
      }
    },
    [
      exportServicesToExcelByIds,
      activeBranchId,
      handleFileData,
      showNotification,
      setLoader,
    ]
  );

  const handleExportToExcelFinancialPnLReport = useCallback(
    async ({
      filterMonth,
      filterYear,
    }: {
      filterMonth: number;
      filterYear: number;
    }) => {
      try {
        const result = await exportToExcelFinancialPnLReport({
          filterMonth,
          filterYear,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        handleFileData(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoader(false);
      }
    },
    [
      activeBranchId,
      exportToExcelFinancialPnLReport,
      handleFileData,
      setLoader,
      showNotification,
    ]
  );

  const handleExportToExcelFinancialIncomesReport = useCallback(
    async ({
      filterMonth,
      filterYear,
    }: {
      filterMonth: number;
      filterYear: number;
    }) => {
      try {
        const result = await exportToExcelFinancialIncomesReport({
          filterMonth,
          filterYear,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        handleFileData(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoader(false);
      }
    },
    [
      activeBranchId,
      exportToExcelFinancialIncomesReport,
      handleFileData,
      setLoader,
      showNotification,
    ]
  );

  const handleExportToExcelFinancialSalaryReport = useCallback(
    async ({
      filterMonth,
      filterYear,
    }: {
      filterMonth: number;
      filterYear: number;
    }) => {
      try {
        const result = await exportToExcelFinancialSalaryReportQuery({
          filterMonth,
          filterYear,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        handleFileData(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoader(false);
      }
    },
    [
      activeBranchId,
      exportToExcelFinancialSalaryReportQuery,
      handleFileData,
      setLoader,
      showNotification,
    ]
  );

  const handleExportToExcelFinancialDetailsOfExpensesReport = useCallback(
    async ({
      filterMonth,
      filterYear,
    }: {
      filterMonth: number;
      filterYear: number;
    }) => {
      try {
        const result = await ExportToExcelFinancialDetailsOfExpensesReport({
          filterMonth,
          filterYear,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        handleFileData(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoader(false);
      }
    },
    [
      ExportToExcelFinancialDetailsOfExpensesReport,
      activeBranchId,
      handleFileData,
      setLoader,
      showNotification,
    ]
  );

  const handleExportToExcelFinancialFactImplReport = useCallback(
    async ({
      filterMonth,
      filterYear,
    }: {
      filterMonth: number;
      filterYear: number;
    }) => {
      try {
        const result = await exportToExcelFinancialFactImpl({
          filterMonth,
          filterYear,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        handleFileData(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoader(false);
      }
    },
    [
      activeBranchId,
      exportToExcelFinancialFactImpl,
      handleFileData,
      setLoader,
      showNotification,
    ]
  );

  const handleExportToExcelFinancialExpensesByArticlesReport = useCallback(
    async ({
      filterMonth,
      filterYear,
    }: {
      filterMonth: number;
      filterYear: number;
    }) => {
      try {
        const result = await exportToExcelFinancialExpensesByArticlesReport({
          filterMonth,
          filterYear,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        handleFileData(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoader(false);
      }
    },
    [
      activeBranchId,
      exportToExcelFinancialExpensesByArticlesReport,
      handleFileData,
      setLoader,
      showNotification,
    ]
  );

  const handleExportToExcelFinancialReports = useCallback(
    async ({
      filterMonth,
      filterYear,
    }: {
      filterMonth: number;
      filterYear: number;
    }) => {
      try {
        const result = await exportToExcelFinancialReports({
          filterMonth,
          filterYear,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        handleFileData(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoader(false);
      }
    },
    [
      activeBranchId,
      exportToExcelFinancialReports,
      handleFileData,
      setLoader,
      showNotification,
    ]
  );

  return {
    exportToExcelByIds,
    exportClientPaymentsToExcel,
    handleExportToExcelFinancialPnLReport,
    handleExportToExcelFinancialSalaryReport,
    handleExportToExcelFinancialDetailsOfExpensesReport,
    handleExportToExcelFinancialIncomesReport,
    handleExportToExcelFinancialFactImplReport,
    handleExportToExcelFinancialExpensesByArticlesReport,
    handleExportToExcelFinancialReports,
  };
};
